// Configuración → Tiendas (coordinador/admin). Dos pestañas:
//   • Destinos: catálogo de destinos del modelo por corrida (D1, Jeronimos, …).
//   • Capacidades: curación SKU×destino + insumos (sticker/termo). La meta es de
//     500 unds/hora por defecto para todos (no se mide aún); cuando haya data se
//     cargará el override por SKU. Los SKUs se reutilizan del catálogo del tenant.
// Ver docs/spec-mvp/modelo/captura-tiendas.md

import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import type { Tienda, TiendaSkuCatalogo } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Tab, Tabs } from '../../components/ui/tabs';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Icon } from '../../components/ui/icon';
import {
  useActualizarTienda,
  useActualizarTiendaSku,
  useCostoInsumo,
  useCostoManoObra,
  useCrearTienda,
  useCrearTiendaSku,
  useEliminarTiendaSku,
  useFijarCostoInsumo,
  useMe,
  useTiendaCapacidades,
  useTiendaSkus,
  useTiendas,
} from '../../lib/api/queries';
import type { CostoInsumoTipo } from 'schemas';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api/client';

const hoyISO = () => new Date().toISOString().slice(0, 10);
const fmtCop = (n: number) => `$${Math.round(n).toLocaleString('es-CO')}`;

export function ConfiguracionTiendasPage() {
  const { state } = useAuth();
  const [tab, setTab] = useState<'destinos' | 'capacidades'>('destinos');
  const [costoDialog, setCostoDialog] = useState<CostoInsumoTipo | null>(null);

  const me = useMe();
  const puedeVerCostos = me.data?.ctx.puede_ver_costos ?? false;

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Tiendas</h1>
            <p className="text-sm text-fg-tertiary">Destinos y SKUs para la captura por corrida.</p>
          </div>
          {puedeVerCostos ? (
            <div className="flex flex-wrap items-center gap-2">
              <CostoInsumoPill tipo="sticker" onClick={() => setCostoDialog('sticker')} />
              <CostoInsumoPill tipo="termo" onClick={() => setCostoDialog('termo')} />
              <HoraHombrePillReadonly />
            </div>
          ) : null}
        </header>

        <Tabs value={tab} onChange={(v) => setTab(v as typeof tab)}>
          <Tab value="destinos">Destinos</Tab>
          <Tab value="capacidades">Capacidades</Tab>
        </Tabs>

        {tab === 'destinos' ? <DestinosTab /> : <CapacidadesTab />}
      </div>
      {costoDialog ? (
        <CostoInsumoDialog tipo={costoDialog} onClose={() => setCostoDialog(null)} />
      ) : null}
    </RoleLayout>
  );
}

const INSUMO_LABEL: Record<CostoInsumoTipo, string> = { sticker: 'sticker', termo: 'termo' };

/** Pill del header: muestra la tarifa vigente del insumo (se cobra adicional
 *  al cliente por cada sticker/termo gastado) y abre el dialog. */
function CostoInsumoPill({ tipo, onClick }: { tipo: CostoInsumoTipo; onClick: () => void }) {
  const { data } = useCostoInsumo();
  const vig = data?.vigentes.find((v) => v.tipo === tipo);
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Tarifa unitaria de ${INSUMO_LABEL[tipo]} — se cobra adicional al cliente`}
      className="flex items-center gap-2 rounded-md border border-border bg-surface-overlay/40 px-3 py-2 text-sm transition-colors hover:bg-surface-overlay"
    >
      <span className="text-fg-tertiary">Tarifa {INSUMO_LABEL[tipo]}</span>
      <span className="font-medium tabular-nums text-fg">
        {vig ? fmtCop(vig.valor_unitario_cop) : '—'}
      </span>
      <Icon name="edit" size={12} className="text-fg-tertiary" />
    </button>
  );
}

/** Pill read-only de hora-hombre (se edita en SKUs y capacidades / C10). */
function HoraHombrePillReadonly() {
  const { data } = useCostoManoObra();
  const v = data?.vigente?.valor_hora_persona_cop ?? null;
  return (
    <div
      title="El costo de mano de obra se edita en SKUs y capacidades"
      className="flex items-center gap-2 rounded-md border border-border bg-surface-overlay/20 px-3 py-2 text-sm opacity-70"
    >
      <span className="text-fg-tertiary">Hora-hombre</span>
      <span className="font-medium tabular-nums text-fg-secondary">
        {v !== null ? fmtCop(v) : '—'}
      </span>
    </div>
  );
}

function CostoInsumoDialog({ tipo, onClose }: { tipo: CostoInsumoTipo; onClose: () => void }) {
  const { data, isLoading } = useCostoInsumo();
  const fijar = useFijarCostoInsumo();
  const [valor, setValor] = useState('');
  const [desde, setDesde] = useState(hoyISO());
  const [verHist, setVerHist] = useState(false);

  const vigente = data?.vigentes.find((v) => v.tipo === tipo) ?? null;
  const historico = (data?.historico ?? []).filter((h) => h.tipo === tipo);

  const guardar = async () => {
    const n = Number(valor);
    if (!n || n <= 0) {
      toast.error('El valor debe ser mayor a 0');
      return;
    }
    try {
      await fijar.mutateAsync({ tipo, valor_unitario_cop: n, vigente_desde: desde });
      toast.success(`Tarifa de ${INSUMO_LABEL[tipo]} actualizada`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Tarifa de {INSUMO_LABEL[tipo]} (se cobra al cliente)</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2 text-sm">
            {isLoading ? (
              <Spinner className="size-4" />
            ) : vigente ? (
              <>
                <span className="text-lg font-medium tabular-nums text-fg">
                  {fmtCop(vigente.valor_unitario_cop)}
                </span>{' '}
                <span className="text-fg-tertiary">/unidad</span>
                <div className="text-[11px] text-fg-tertiary">
                  vigente desde {vigente.vigente_desde}
                </div>
              </>
            ) : (
              <span className="text-fg-tertiary">Sin tarifa definida todavía.</span>
            )}
          </div>

          <p className="text-[11px] text-warn">
            Se crea una nueva vigencia — el valor anterior se conserva en el histórico.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ci-valor">Nuevo valor *</Label>
              <Input
                id="ci-valor"
                type="number"
                min="0"
                step="0.01"
                className="tabular-nums"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder={tipo === 'sticker' ? '60' : '354.6'}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ci-desde">Vigente desde *</Label>
              <Input
                id="ci-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setVerHist((p) => !p)}
            className="flex items-center gap-1 self-start text-[11px] text-fg-secondary hover:text-fg"
          >
            <Icon name="clock" size={12} /> {verHist ? 'Ocultar histórico' : 'Ver histórico'}
          </button>
          {verHist ? (
            <div className="max-h-40 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="bg-surface-overlay text-left text-fg-tertiary">
                  <tr>
                    <th className="px-2 py-1">Valor</th>
                    <th className="px-2 py-1">Desde</th>
                    <th className="px-2 py-1">Hasta</th>
                    <th className="px-2 py-1">Cambió</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((h) => (
                    <tr key={h.id} className="border-t border-border">
                      <td className="px-2 py-1 tabular-nums text-fg">
                        {fmtCop(h.valor_unitario_cop)}
                      </td>
                      <td className="px-2 py-1">{h.vigente_desde}</td>
                      <td className="px-2 py-1">
                        {h.vigente_hasta ?? <span className="text-ok">vigente</span>}
                      </td>
                      <td className="px-2 py-1 text-fg-tertiary">
                        {h.registrado_por_nombre ?? '—'}
                      </td>
                    </tr>
                  ))}
                  {historico.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-2 py-2 text-center text-fg-tertiary">
                        Sin histórico.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardar} loading={fijar.isPending}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Destinos — catálogo (crear, renombrar, activar/desactivar).
// ---------------------------------------------------------------------------
function DestinosTab() {
  const { data, isLoading, isError, error } = useTiendas(true); // incluye inactivas
  const crear = useCrearTienda();
  const actualizar = useActualizarTienda();

  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');

  const onCrear = async () => {
    if (!codigo.trim() || !nombre.trim()) {
      toast.error('Código y nombre son obligatorios.');
      return;
    }
    try {
      await crear.mutateAsync({ codigo: codigo.trim(), nombre: nombre.trim(), es_activa: true });
      toast.success('Tienda creada.');
      setCodigo('');
      setNombre('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo crear la tienda.');
    }
  };

  const onToggle = async (t: Tienda) => {
    try {
      await actualizar.mutateAsync({ id: t.id, patch: { es_activa: !t.es_activa } });
      toast.success(t.es_activa ? 'Tienda desactivada.' : 'Tienda activada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo actualizar.');
    }
  };

  const onRenombrar = async (t: Tienda, nuevoNombre: string) => {
    const v = nuevoNombre.trim();
    if (!v || v === t.nombre) return;
    try {
      await actualizar.mutateAsync({ id: t.id, patch: { nombre: v } });
      toast.success('Nombre actualizado.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo actualizar.');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-32">
            <Label htmlFor="codigo">Código</Label>
            <Input
              id="codigo"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="D1"
              className="mt-1"
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <Label htmlFor="nombre">Nombre</Label>
            <Input
              id="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Discount"
              className="mt-1"
            />
          </div>
          <Button onClick={onCrear} disabled={crear.isPending}>
            {crear.isPending ? 'Creando…' : 'Agregar destino'}
          </Button>
        </CardContent>
      </Card>

      {isError ? (
        <p className="text-sm text-crit">
          {error instanceof Error ? error.message : 'Error al cargar las tiendas.'}
        </p>
      ) : isLoading || !data ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-6" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Código</TH>
                  <TH>Nombre</TH>
                  <TH align="right">Estado</TH>
                </TR>
              </THead>
              <TBody>
                {data.length === 0 ? (
                  <TR>
                    <TD colSpan={3} className="py-6 text-center text-fg-tertiary">
                      Aún no hay tiendas. Cree el primer destino arriba.
                    </TD>
                  </TR>
                ) : (
                  data.map((t) => (
                    <FilaTienda
                      key={t.id}
                      tienda={t}
                      onRenombrar={onRenombrar}
                      onToggle={onToggle}
                      guardando={actualizar.isPending}
                    />
                  ))
                )}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FilaTienda({
  tienda,
  onRenombrar,
  onToggle,
  guardando,
}: {
  tienda: Tienda;
  onRenombrar: (t: Tienda, nombre: string) => void;
  onToggle: (t: Tienda) => void;
  guardando: boolean;
}) {
  const [nombre, setNombre] = useState(tienda.nombre);
  // Re-sincroniza si cambia desde el server (p. ej. tras invalidar la query).
  useEffect(() => setNombre(tienda.nombre), [tienda.nombre]);

  return (
    <TR>
      <TD className="font-mono text-xs uppercase tracking-wide text-fg-tertiary">
        {tienda.codigo}
      </TD>
      <TD>
        <Input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          onBlur={() => onRenombrar(tienda, nombre)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onRenombrar(tienda, nombre);
          }}
          className="h-8"
        />
      </TD>
      <TD align="right">
        <div className="flex items-center justify-end gap-2">
          {tienda.es_activa ? (
            <Badge tone="ok">Activa</Badge>
          ) : (
            <Badge tone="neutral">Inactiva</Badge>
          )}
          <Button variant="outline" onClick={() => onToggle(tienda)} disabled={guardando}>
            {tienda.es_activa ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      </TD>
    </TR>
  );
}

// ---------------------------------------------------------------------------
// Pestaña Capacidades — curación SKU×destino + insumos (sticker/termo).
// ---------------------------------------------------------------------------
function CapacidadesTab() {
  const tiendas = useTiendas(); // solo activas para curar
  const skus = useTiendaSkus();
  const capacidades = useTiendaCapacidades();
  const crear = useCrearTiendaSku();
  const actualizar = useActualizarTiendaSku();
  const eliminar = useEliminarTiendaSku();

  const insumos = useCostoInsumo();
  const [tiendaId, setTiendaId] = useState('');
  const [skuId, setSkuId] = useState('');
  const [cantSticker, setCantSticker] = useState('0');
  const [cantTermo, setCantTermo] = useState('0');
  // Destinos expandidos (colapsados por defecto para que la lista no abrume).
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const toggleDestino = (id: string) =>
    setExpandidos((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const costoSticker =
    insumos.data?.vigentes.find((v) => v.tipo === 'sticker')?.valor_unitario_cop ?? 0;
  const costoTermo =
    insumos.data?.vigentes.find((v) => v.tipo === 'termo')?.valor_unitario_cop ?? 0;

  const skuOptions: ComboboxOption[] = useMemo(
    () =>
      (skus.data ?? []).map((s) => ({
        value: s.id,
        label: s.codigo_cliente,
        description: s.descripcion,
        searchText: `${s.codigo_cliente} ${s.descripcion}`,
      })),
    [skus.data],
  );

  // Curación agrupada por destino (solo destinos activos visibles arriba).
  const porTienda = useMemo(() => {
    const map = new Map<string, TiendaSkuCatalogo[]>();
    for (const c of capacidades.data ?? []) {
      const arr = map.get(c.tienda_id) ?? [];
      arr.push(c);
      map.set(c.tienda_id, arr);
    }
    return map;
  }, [capacidades.data]);

  const onAgregar = async () => {
    if (!tiendaId || !skuId) {
      toast.error('Elija destino y SKU.');
      return;
    }
    try {
      await crear.mutateAsync({
        tienda_id: tiendaId,
        sku_id: skuId,
        cantidad_stickers: Math.max(0, Math.trunc(Number(cantSticker) || 0)),
        cantidad_termos: Math.max(0, Math.trunc(Number(cantTermo) || 0)),
      });
      toast.success('SKU agregado al destino.');
      setSkuId('');
      setCantSticker('0');
      setCantTermo('0');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo agregar.');
    }
  };

  const onSetCantidad = async (
    c: TiendaSkuCatalogo,
    campo: 'cantidad_stickers' | 'cantidad_termos',
    valor: number,
  ) => {
    const v = Math.max(0, Math.trunc(valor));
    if (v === c[campo]) return;
    try {
      await actualizar.mutateAsync({ id: c.id, patch: { [campo]: v } });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo actualizar.');
    }
  };

  const onEliminar = async (c: TiendaSkuCatalogo) => {
    try {
      await eliminar.mutateAsync(c.id);
      toast.success('SKU quitado del destino.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'No se pudo quitar.');
    }
  };

  const cargando = tiendas.isLoading || skus.isLoading || capacidades.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-tertiary">
        Defina qué SKUs aplican a cada destino e indique cuántos stickers y termos lleva cada caja
        (0 = no usa). El costo en pesos por caja se calcula con los costos unitarios de arriba.
      </p>

      {/* Agregar curación */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-44">
            <Label htmlFor="cap-destino">Destino</Label>
            <Select
              id="cap-destino"
              value={tiendaId}
              onChange={(e) => setTiendaId(e.target.value)}
              className="mt-1"
            >
              <option value="">— Elija —</option>
              {(tiendas.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[220px] flex-1">
            <Label>SKU</Label>
            <Combobox
              options={skuOptions}
              value={skuId}
              onChange={setSkuId}
              placeholder="Buscar SKU…"
            />
          </div>
          <div className="w-24">
            <Label htmlFor="cap-stk"># Sticker</Label>
            <Input
              id="cap-stk"
              type="number"
              min="0"
              className="mt-1 h-9 tabular-nums"
              value={cantSticker}
              onChange={(e) => setCantSticker(e.target.value)}
            />
          </div>
          <div className="w-24">
            <Label htmlFor="cap-trm"># Termo</Label>
            <Input
              id="cap-trm"
              type="number"
              min="0"
              className="mt-1 h-9 tabular-nums"
              value={cantTermo}
              onChange={(e) => setCantTermo(e.target.value)}
            />
          </div>
          <Button onClick={onAgregar} disabled={crear.isPending}>
            {crear.isPending ? 'Agregando…' : 'Agregar SKU'}
          </Button>
        </CardContent>
      </Card>

      {/* Listado por destino */}
      {capacidades.isError ? (
        <p className="text-sm text-crit">No se pudo cargar la curación de SKUs.</p>
      ) : cargando ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-6" />
        </div>
      ) : (tiendas.data ?? []).length === 0 ? (
        <p className="text-sm text-fg-tertiary">
          Cree primero los destinos en la pestaña anterior.
        </p>
      ) : (
        <>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() =>
                setExpandidos(
                  expandidos.size === (tiendas.data ?? []).length
                    ? new Set()
                    : new Set((tiendas.data ?? []).map((t) => t.id)),
                )
              }
            >
              {expandidos.size === (tiendas.data ?? []).length ? 'Contraer todo' : 'Expandir todo'}
            </Button>
          </div>
          {(tiendas.data ?? []).map((t) => {
            const filas = porTienda.get(t.id) ?? [];
            const abierto = expandidos.has(t.id);
            const conSticker = filas.filter((c) => c.cantidad_stickers > 0).length;
            const conTermo = filas.filter((c) => c.cantidad_termos > 0).length;
            return (
              <Card key={t.id}>
                <CardContent className="p-0">
                  <button
                    type="button"
                    onClick={() => toggleDestino(t.id)}
                    aria-expanded={abierto}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-surface-overlay/40"
                  >
                    <Icon
                      name={abierto ? 'chevron-down' : 'chevron-right'}
                      size={14}
                      className="text-fg-tertiary"
                    />
                    <h2 className="text-sm font-medium text-fg">{t.nombre}</h2>
                    <span className="ml-auto flex items-center gap-3 text-xs text-fg-tertiary">
                      <span>
                        {conSticker} con sticker · {conTermo} con termo
                      </span>
                      <Badge tone="neutral">
                        {filas.length} SKU{filas.length === 1 ? '' : 's'}
                      </Badge>
                    </span>
                  </button>
                  {abierto ? (
                    <Table>
                      <THead>
                        <TR>
                          <TH>Código</TH>
                          <TH>Descripción</TH>
                          <TH align="right"># Sticker</TH>
                          <TH align="right"># Termo</TH>
                          <TH align="right">Insumo/caja</TH>
                          <TH align="right">Acción</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {filas.length === 0 ? (
                          <TR>
                            <TD colSpan={6} className="py-4 text-center text-fg-tertiary">
                              Sin SKUs asignados a este destino.
                            </TD>
                          </TR>
                        ) : (
                          filas.map((c) => (
                            <FilaCapacidad
                              key={c.id}
                              c={c}
                              costoSticker={costoSticker}
                              costoTermo={costoTermo}
                              onSet={onSetCantidad}
                              onEliminar={onEliminar}
                              saving={actualizar.isPending}
                              eliminando={eliminar.isPending}
                            />
                          ))
                        )}
                      </TBody>
                    </Table>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}

// Fila de curación SKU×destino con cantidades de insumo editables (commit onBlur)
// + costo de insumo por caja derivado de los costos unitarios vigentes.
function FilaCapacidad({
  c,
  costoSticker,
  costoTermo,
  onSet,
  onEliminar,
  saving,
  eliminando,
}: {
  c: TiendaSkuCatalogo;
  costoSticker: number;
  costoTermo: number;
  onSet: (
    c: TiendaSkuCatalogo,
    campo: 'cantidad_stickers' | 'cantidad_termos',
    valor: number,
  ) => void;
  onEliminar: (c: TiendaSkuCatalogo) => void;
  saving: boolean;
  eliminando: boolean;
}) {
  const [stk, setStk] = useState(String(c.cantidad_stickers));
  const [trm, setTrm] = useState(String(c.cantidad_termos));
  useEffect(() => {
    setStk(String(c.cantidad_stickers));
    setTrm(String(c.cantidad_termos));
  }, [c.cantidad_stickers, c.cantidad_termos]);

  const insumoCaja = c.cantidad_stickers * costoSticker + c.cantidad_termos * costoTermo;

  return (
    <TR>
      <TD className="font-mono text-xs text-fg-tertiary">{c.sku_codigo}</TD>
      <TD className="text-fg">{c.sku_descripcion}</TD>
      <TD align="right">
        <Input
          type="number"
          min="0"
          value={stk}
          onChange={(e) => setStk(e.target.value)}
          onBlur={() => onSet(c, 'cantidad_stickers', Number(stk) || 0)}
          disabled={saving}
          aria-label="Cantidad de stickers por caja"
          className="ml-auto h-8 w-16 text-right tabular-nums"
        />
      </TD>
      <TD align="right">
        <Input
          type="number"
          min="0"
          value={trm}
          onChange={(e) => setTrm(e.target.value)}
          onBlur={() => onSet(c, 'cantidad_termos', Number(trm) || 0)}
          disabled={saving}
          aria-label="Cantidad de termos por caja"
          className="ml-auto h-8 w-16 text-right tabular-nums"
        />
      </TD>
      <TD align="right" className="tabular-nums text-fg-secondary">
        {insumoCaja > 0 ? fmtCop(insumoCaja) : '—'}
      </TD>
      <TD align="right">
        <button
          type="button"
          onClick={() => onEliminar(c)}
          disabled={eliminando}
          className="text-xs text-fg-tertiary underline disabled:opacity-40"
        >
          Quitar
        </button>
      </TD>
    </TR>
  );
}
